import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useContext, useEffect, useState } from 'react';
import {
    AlertColor,
    Box,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { DependencyInjectionContext } from '../../../contexts/dependency-injection-context';
import { LoadingIndicator } from '../../../components/core/LoadingIndicator/LoadingIndicator';
import { ClientSelector } from '../../../components/core/ClientSelector/ClientSelector';
import { WelcomeBoardLayout as WelcomeBoardLayoutItem } from '../../../common/interfaces';
import { LayoutEditor } from './LayoutEditor';
import { DROPDOWN_HEIGHT, DROPDOWN_MARGIN, DROPDOWN_PADDING } from './constants';

export const WelcomeBoardLayout = () => {
    const { instance } = useMsal();
    const { formsApi, applicationState } = useContext(DependencyInjectionContext);

    // Loading states
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [layouts, setLayouts] = useState<WelcomeBoardLayoutItem[]>([]);
    const [selectedLayoutIndex, setSelectedLayoutIndex] = useState<number>(0);

    // Alert state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertText, setAlertText] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertColor>('success');

    // Client selection state
    const [selectedClientId, setSelectedClientId] = useState<string>();

    const handleCloseAlert = () => {
        setAlertOpen(false);
    };

    const showAlert = (message: string, type: AlertColor = 'success') => {
        setAlertText(message);
        setAlertType(type);
        setAlertOpen(true);
    };

    const loadLayoutConfig = async () => {
        setLoading(true);

        try {
            const data = await formsApi.get<WelcomeBoardLayoutItem[]>(`GetWelcomeBoardLayouts?clientId=${applicationState.selectedClientId || ''}`);
            if (data && data.length > 0) {
                setLayouts(data);
                setSelectedLayoutIndex(0);
                showAlert('Layout configuration loaded successfully!', 'success');
            } else {
                // Create default layout if none exists
                const defaultLayout: WelcomeBoardLayoutItem = {
                    id: null,
                    clientId: applicationState.selectedClientId || '',
                    name: 'Default Layout',
                    gridColumnsTemplate: 'repeat(3, 1fr)',
                    gridRowsTemplate: 'repeat(3, 1fr)',
                    welcomeBoardItems: []
                };
                setLayouts([defaultLayout]);
                setSelectedLayoutIndex(0);
            }
        } catch (error) {
            console.error('Error loading welcome board:', error);
            showAlert('Error loading welcome board configuration', 'error');
            
            // Fallback to default layout
            const defaultLayout: WelcomeBoardLayoutItem = {
                id: null,
                clientId: applicationState.selectedClientId || '',
                name: 'Default Layout',
                gridColumnsTemplate: 'repeat(3, 1fr)',
                gridRowsTemplate: 'repeat(3, 1fr)',
                welcomeBoardItems: []
            };
            setLayouts([defaultLayout]);
            setSelectedLayoutIndex(0);
        } finally {
            setLoading(false);
        }
    };

    const onClientSelected = async (clientId: string) => {
        setSelectedClientId(clientId);
        applicationState.selectedClientId = clientId;
        // Reset layout selection when client changes
        setSelectedLayoutIndex(0);
        // Reload layout config for the selected client
        await loadLayoutConfig();
    };

    const onLayoutSelected = (index: number) => {
        setSelectedLayoutIndex(index);
    };

    useEffect(() => {
        loadLayoutConfig();
    }, [formsApi, applicationState.selectedClientId]);

    const handleSave = async (layout: WelcomeBoardLayoutItem) => {
        setSaving(true);
        try {
            // Update with current client ID
            const layoutToSave = {
                ...layout,
                clientId: applicationState.selectedClientId || ''
            };

            // Create request payload matching the API endpoint structure
            const requestPayload = {
                welcomeBoardLayout: layoutToSave,
                clientId: applicationState.selectedClientId || ''
            };

            const response = await formsApi.post<unknown, WelcomeBoardLayoutItem>('/UpsertWelcomeBoardLayout', requestPayload);
            if (response) {
                // Update the layouts array with the saved layout
                const updatedLayouts = [...layouts];
                updatedLayouts[selectedLayoutIndex] = response;
                setLayouts(updatedLayouts);
                showAlert('Layout saved successfully!', 'success');
            }
        } catch (error) {
            console.error('Error saving layout:', error);
            showAlert('Error saving layout configuration', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ height: '100vh', overflow: 'hidden', width: '100vw', display: 'flex', flexDirection: 'column' }}>
            <AuthenticatedTemplate>
                <Box sx={{ p: `${DROPDOWN_PADDING}px`, display: 'flex', mt: `${DROPDOWN_MARGIN}px`, height: `${DROPDOWN_HEIGHT}px` }}>
                    <ClientSelector onClientSelected={onClientSelected} />
                    
                    {layouts.length > 0 && (
                        <FormControl sx={{ minWidth: 200, marginLeft: '2rem' }}>
                            <InputLabel>Select Layout</InputLabel>
                            <Select
                                value={selectedLayoutIndex}
                                label="Select Layout"
                                onChange={(e) => onLayoutSelected(Number(e.target.value))}
                            >
                                {layouts.map((layout, index) => (
                                    <MenuItem key={index} value={index}>
                                        {layout.name || `Layout ${index + 1}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                
                {loading ? (
                    <LoadingIndicator loading={loading} />
                ) : layouts.length > 0 && layouts[selectedLayoutIndex] ? (
                    <LayoutEditor
                        initialLayout={layouts[selectedLayoutIndex]}
                        onSave={handleSave}
                    />
                ) : null}

                <Snackbar
                    open={alertOpen}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseAlert} severity={alertType} sx={{ width: '100%' }}>
                        {alertText}
                    </Alert>
                </Snackbar>

                <LoadingIndicator loading={saving} />
            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <p><a href="#" onClick={() => instance.loginRedirect()}>Sign In</a> to access Welcome Board Layout!</p>
                </Box>
            </UnauthenticatedTemplate>
        </Box>
    );
};